"use client";

import { type InputHTMLAttributes, useRef, type FC, type Ref } from "react";
import { mergeClasses } from "@huub-web/utils";
import type { RecipeVariants } from "@vanilla-extract/recipes";

import type { ActiveProp, ClassNameProp, ValidatableProps } from "../../props";
import { useFormValidation } from "../../hooks/useFormValidation";
import { mergeRefs } from "../../utils";
import { Flex } from "../Flex";
import { Text } from "../Text";
import { Reveal } from "../Reveal";
import { Badge } from "../Badge";
import { Icon } from "../Icon";

import {
  containerStyle,
  radioButtonStyle,
  radioInputStyle,
  radioCircleStyle,
  radioInnerCircleStyle,
  labelStyle,
  radioSuccessStyle,
} from "./RadioButtons.css";
import { borders } from "../../theme/css/borders.css";
import { focusWithin } from "../../theme/css/focusVisible.css";

export type RadioOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type RadioButtonsChangeHandler = (value: string) => void;

export type RadioButtonsProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value" | "type"
> &
  RecipeVariants<typeof radioButtonStyle> &
  ValidatableProps &
  ActiveProp &
  ClassNameProp & {
    /**
     * The current selected value
     */
    value?: string;
    /**
     * Array of radio options
     */
    options: RadioOption[];
    /**
     * Orientation of the radio buttons
     */
    orientation?: "horizontal" | "vertical";
    /**
     * Whether to highlight the radio buttons
     */
    highlight?: boolean;
    /**
     * Whether to show success state
     */
    success?: boolean;
    /**
     * Handler for value change
     */
    onChange?: RadioButtonsChangeHandler;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * RadioButtons component for selecting a single option from a list.
 */
export const RadioButtons: FC<RadioButtonsProps> = ({
  id: idProp,
  disabled: disabledProp,
  error: errorProp,
  validation,
  name: nameProp,
  orientation = "vertical",
  highlight,
  success,
  value,
  options,
  active,
  onChange,
  className,
  ref,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { id, error, disabled } = useFormValidation(value, {
    id: idProp,
    error: errorProp,
    disabled: disabledProp,
    validation,
    name: nameProp,
    ref: inputRef,
  });

  // Use the id as the name for radio button grouping, or fall back to nameProp
  const radioGroupName = nameProp || id || "radio-group";

  const handleChange = (optionValue: string) => {
    if (!disabled) {
      onChange?.(optionValue);
    }
  };

  return (
    <Flex
      ref={ref}
      className={mergeClasses(containerStyle({ orientation }), className)}
      direction={orientation === "horizontal" ? "row" : "column"}
      role="radiogroup"
      {...(active && { "aria-active": true })}
    >
      {options.map((option, index) => {
        const optionID = index === 0 ? id : `${id}-${index}`;
        const isChecked = value === option.value;
        const isDisabled = disabled || option.disabled;

        return (
          <Flex
            key={option.value}
            className={radioButtonStyle({
              checked: isChecked,
              disabled: isDisabled,
              success,
              highlight,
              error,
            })}
            align="flex-start"
          >
            <label
              htmlFor={optionID}
              className={mergeClasses(
                radioCircleStyle({
                  checked: isChecked,
                  disabled: isDisabled,
                  success,
                  highlight,
                  error,
                }),
                focusWithin,
                borders.default,
              )}
              {...(isDisabled && { "aria-disabled": true })}
            >
              <input
                ref={index === 0 ? mergeRefs(inputRef) : undefined}
                id={optionID}
                type="radio"
                name={radioGroupName}
                value={option.value}
                checked={isChecked}
                className={radioInputStyle}
                onChange={() => handleChange(option.value)}
                disabled={isDisabled}
                {...props}
              />
              <div
                className={radioInnerCircleStyle({
                  checked: isChecked,
                })}
              />
            </label>

            <Text
              as="label"
              htmlFor={optionID}
              className={labelStyle({ disabled: isDisabled })}
              variant="bodyS"
              color="currentColor75"
            >
              {option.label}
            </Text>

            {isChecked && success && (
              <Reveal>
                <Badge
                  variant="success"
                  size="xs"
                  value={<Icon icon="checkThick" size="xs" />}
                  className={radioSuccessStyle}
                  inline
                />
              </Reveal>
            )}
          </Flex>
        );
      })}
    </Flex>
  );
};
